import React, {
  type ElementType,
  type FC,
  type IframeHTMLAttributes,
  memo,
  useMemo,
} from "react";

import {
  Alert,
  Box,
  Row,
  AudienceIcon,
  JourneyIcon,
  DestinationIcon,
  SourceIcon,
  ModelIcon,
  Heading,
  Text,
  Column,
  Badge,
  ExternalLinkIcon,
  TextWithTooltip,
} from "@hightouch/htui";
import { Link } from "src/router";
import type { Components } from "react-markdown";
import rehypeRaw from "rehype-raw";
import { isEqual } from "lodash";
import { Markdown, type MarkdownProps } from ".";
import { AccordionSection } from "src/components/accordion-section";
import { parseHeadingLevel } from "./heading-level";
import { rehypeSectionWrap } from "./rehype-section-wrap";
import { BarChart } from "./bar-chart";
import { LineChart } from "./line-chart";
import { Heatmap } from "./heatmap";
import { FunnelChart } from "./funnel-chart";
import { Card as AppCard } from "src/components/card/card";
import { AdPerformanceTableEmbed } from "src/pages/ad-studio/insights/ad-performance-table-embed";
import { AdCardGridEmbed } from "src/pages/ad-studio/insights/ad-card-grid-embed";
import { drilldownAttrsSchema } from "src/pages/ad-studio/insights/performance/utils/embeds";
import type { Grain } from "src/pages/ad-studio/insights/performance/types";
import { DrilldownEmbed } from "src/pages/ad-studio/insights/drilldown-embed";
import { InsightCard, Actions, Action } from "./cards/insight-card";
import { parseSafeHttpUrl } from "./safe-url";
import { AutoFitSvg } from "./auto-fit-svg";
import { InlineCitations } from "src/components/llm/session-context/inline-citations";

const DrilldownTag = ({
  grain,
  attrs,
}: {
  grain: Grain;
  attrs: Record<string, string>;
}) => {
  const drilldown = drilldownAttrsSchema(grain).safeParse(attrs);
  return drilldown.success ? (
    <DrilldownEmbed attrs={drilldown.data} />
  ) : (
    <Alert
      type="error"
      title="Couldn't show this drilldown"
      message="This drilldown is missing its ad platform or the entity to show. Try again, or contact support if it keeps happening."
    />
  );
};

type IframeRendererProps = IframeHTMLAttributes<HTMLIFrameElement> & {
  node?: unknown;
};

// Agent-emitted embeds (e.g. a YouTube ad creative). Force a referrer policy
// on the iframe itself: the prod app sends `Referrer-Policy: same-origin`,
// which strips the referrer YouTube requires and makes its player fail with
// "Error 153"; the iframe's own policy overrides the document default so the
// embed still gets the site origin. Declared at module scope for a stable
// component identity, so react-markdown doesn't remount (and reload) the embed
// on every render. Localhost sends no such header, so this is a prod-only
// failure.
const ReferrerSafeIframe: FC<IframeRendererProps> = ({
  node: _node,
  title,
  ...iframeProps
}) => (
  <iframe
    {...iframeProps}
    title={typeof title === "string" ? title : "Embedded content"}
    referrerPolicy="strict-origin-when-cross-origin"
  />
);

const CardGrid = ({ children }: { children: React.ReactNode }) => {
  return (
    <Box
      display="grid"
      gridTemplateColumns="repeat(auto-fit, minmax(240px,1fr))"
      gap={4}
      my={4}
    >
      {children}
    </Box>
  );
};

// Module scope keeps these renderers' identities stable across renders.
const STATIC_COMPONENTS = {
  "collapsible-heading": ({ children, title, level }) => {
    const { as, size } = parseHeadingLevel(level);
    return (
      <AccordionSection
        noPadding={true}
        keepContentMounted
        label={
          <Heading as={as} size={size}>
            {title}
          </Heading>
        }
        buttonSx={{ my: 2 }}
      >
        <Box pl={6}>{children}</Box>
      </AccordionSection>
    );
  },
  "collapsible-text": ({ children, title }) => (
    <AccordionSection
      noPadding={true}
      keepContentMounted
      label={<Text>{title}</Text>}
    >
      <Box pl={6}>{children}</Box>
    </AccordionSection>
  ),
  // TODO: remove this, all collapsible sections should be <collapsible-heading> or <collapsible-text>
  collapsible: ({ children, title }) => (
    <AccordionSection
      noPadding={true}
      keepContentMounted
      label={<Heading>{title}</Heading>}
      buttonSx={{ my: 2 }}
    >
      <Box pl={6}>{children}</Box>
    </AccordionSection>
  ),
  resource: ({ children, type, id }) => (
    <ResourceLink type={type} id={id}>
      {children}
    </ResourceLink>
  ),
  "bar-chart": BarChart,
  "line-chart": LineChart,
  heatmap: Heatmap,
  funnel: FunnelChart, // Legacy support for old chats
  "funnel-chart": FunnelChart,
  // Always registered; the agent only emits these via the
  // initiative-insights skill (the agenticInitiatives-gated flow). Hosts
  // that own the frame override `insight-card` via the `components` prop.
  "insight-card": InsightCard,
  actions: Actions,
  action: Action,
  "card-grid": CardGrid,
  "ad-performance-table": (attrs: Record<string, string>) => (
    <AdPerformanceTableEmbed attrs={attrs} />
  ),
  "ad-card-grid": (attrs: Record<string, string>) => (
    <AdCardGridEmbed attrs={attrs} />
  ),
  "campaign-drilldown": (attrs: Record<string, string>) => (
    <DrilldownTag grain="campaign" attrs={attrs} />
  ),
  "ad-set-drilldown": (attrs: Record<string, string>) => (
    <DrilldownTag grain="ad_set" attrs={attrs} />
  ),
  "ad-drilldown": (attrs: Record<string, string>) => (
    <DrilldownTag grain="ad" attrs={attrs} />
  ),
  "image-grid": ({ children }) => (
    <Box
      display="grid"
      gridTemplateColumns="repeat(3, 1fr)"
      gap={2}
      my={4}
      sx={{
        // Ensure paragraph tags don't break the grid layout
        "& > p": {
          margin: 0,
          display: "contents",
        },
      }}
    >
      {children}
    </Box>
  ),
  // Box-content component: a phrase-led block — a short header with a
  // colored badge and an optional description. The number lives in the
  // badge (vs <stat>, where the number is the large element). Meant to
  // be nested inside a <card>.
  headline: ({
    header,
    description,
    "badge-text": badgeText,
    "badge-variant": badgeVariant,
  }) => (
    <Headline
      header={header}
      description={description}
      badgeText={badgeText}
      badgeVariant={boxBadgeVariant(badgeVariant)}
    />
  ),
  // Inline colored pill for a badged word/phrase; several can sit in a
  // row. Text goes in the tag body; `variant` picks the color.
  pill: ({ children, variant }) => (
    <Box as={Badge} variant={pillVariant(variant)}>
      {children}
    </Box>
  ),
  // Inline text badge for highlighting a metric within prose, limited
  // to green/red/gray.
  badge: ({ children, variant }) => (
    <InlineTextBadge variant={inlineBadgeVariant(variant)}>
      {children}
    </InlineTextBadge>
  ),
  // Agent-authored inline SVG figures (custom-html skill): repair
  // viewBoxes that clip content and scale to the container width.
  svg: AutoFitSvg,
  // Forces a referrer policy so agent-emitted embeds keep their
  // referrer in prod; see ReferrerSafeIframe.
  iframe: ReferrerSafeIframe,
  citations: InlineCitations,
} as Components;

/** Every tag this renderer maps to a component, including the ones built per render below. */
export const RENDERED_TAGS: ReadonlySet<string> = new Set([
  ...Object.keys(STATIC_COMPONENTS),
  "hero-card",
  "card",
  "stat",
]);

type Props = MarkdownProps & {
  enableReportSections?: boolean;
  /** Rehype plugins that run on the raw HTML after rehype-raw, such as an allowlist. Must be referentially stable. */
  rehypePluginsAfterRaw?: MarkdownProps["rehypePlugins"];
};

const DangerousMarkdownWithHtmlInternal: FC<Readonly<Props>> = ({
  children,
  variant,
  enableReportSections,
  rehypePluginsAfterRaw,
  components,
  ...props
}) => {
  const isCompact = variant === "compact";
  const rehypePlugins = useMemo(
    () => [
      rehypeRaw,
      ...(rehypePluginsAfterRaw ?? []),
      ...(enableReportSections ? [rehypeSectionWrap] : []),
    ],
    [enableReportSections, rehypePluginsAfterRaw],
  );
  const markdownComponents = useMemo(
    () =>
      ({
        ...STATIC_COMPONENTS,
        // Built per render for `isCompact`; list each tag here in RENDERED_TAGS too.
        "hero-card": ({
          children,
          label,
          "secondary-label": secondaryLabel,
          header,
          description,
          "header-badge-text": headerBadgeText,
          "header-badge-variant": headerBadgeVariant,
        }) => (
          <HeroCard
            isCompact={isCompact}
            label={label}
            secondaryLabel={secondaryLabel}
            header={header}
            description={description}
            headerBadgeText={headerBadgeText}
            headerBadgeVariant={headerBadgeVariant}
          >
            {children}
          </HeroCard>
        ),
        card: ({
          children,
          label,
          header,
          description,
          "header-badge-text": headerBadgeText,
          "header-badge-variant": headerBadgeVariant,
          url,
        }) => (
          <Card
            isCompact={isCompact}
            label={label}
            header={header}
            description={description}
            headerBadgeText={headerBadgeText}
            headerBadgeVariant={headerBadgeVariant}
            url={url}
          >
            {children}
          </Card>
        ),
        // Box-content component: a single large metric with an inline
        // caption and optional badge. Meant to be nested inside a <card>.
        stat: ({
          label,
          value,
          "value-variant": valueVariant,
          description,
          "badge-text": badgeText,
          "badge-variant": badgeVariant,
        }) => (
          <Stat
            isCompact={isCompact}
            label={label}
            value={value}
            valueVariant={valueVariant}
            description={description}
            badgeText={badgeText}
            badgeVariant={boxBadgeVariant(badgeVariant)}
          />
        ),
        // Let callers override/extend the renderer map (e.g. the Ad Studio
        // brief panel swaps `img` for AdStudioImage). Spread last so caller
        // overrides win; omitted by every other consumer.
        ...components,
      }) as Components,
    [isCompact, components],
  );
  return (
    <Markdown
      rehypePlugins={rehypePlugins}
      variant={variant}
      components={markdownComponents}
      {...props}
    >
      {children}
    </Markdown>
  );
};

// Note: this should only be used for trusted content as it will render raw html
export const DangerousMarkdownWithHtml = memo(
  DangerousMarkdownWithHtmlInternal,
  (prevProps, nextProps) => {
    return (
      isEqual(prevProps.children, nextProps.children) &&
      prevProps.enableReportSections === nextProps.enableReportSections &&
      prevProps.rehypePluginsAfterRaw === nextProps.rehypePluginsAfterRaw
    );
  },
);

const getResourceHref = (type: string, id: string): string => {
  switch (type) {
    case "audience":
      return `/audiences/${id}`;
    case "model":
      return `/models/${id}`;
    case "journey":
      return `/journeys/${id}`;
    case "destination":
      return `/destinations/${id}`;
    case "source":
      return `/sources/${id}`;
    default:
      return "#";
  }
};

const resourceIcons: Record<string, ElementType> = {
  audience: AudienceIcon,
  journey: JourneyIcon,
  model: ModelIcon,
  destination: DestinationIcon,
  source: SourceIcon,
};

export const ResourceLink = ({
  children,
  type,
  id,
}: {
  children: React.ReactNode;
  type: string;
  id: string;
}) => {
  return (
    <Box
      display="inline-block"
      as={Link}
      href={getResourceHref(type, id)}
      isExternal
    >
      <Box as={resourceIcons[type]} fontSize="14px" mr={1} />
      {children}
    </Box>
  );
};

const HeroCard = ({
  label,
  secondaryLabel,
  header,
  description,
  headerBadgeText,
  headerBadgeVariant,
  isCompact,
  children,
}: {
  // Every attr is optional — a hero can be composed entirely from children
  // (a <stat>, a chart). Attribute shorthand and children can be combined.
  label?: string;
  secondaryLabel?: string;
  header?: string;
  description?: string;
  headerBadgeText?: string;
  headerBadgeVariant?: "success" | "error";
  isCompact?: boolean;
  children?: React.ReactNode;
}) => {
  const hasHeaderRow = Boolean(header || headerBadgeText);
  const hasChildren = childrenHaveContent(children);

  return (
    <AppCard my={4} px={4} py={isCompact ? 2 : 4}>
      <Column gap={2}>
        {(label || secondaryLabel) && (
          <Row justifyContent="space-between" align="center">
            <Text color="text.secondary">{label}</Text>
            {secondaryLabel && (
              <Text size="sm" color="text.secondary">
                {secondaryLabel}
              </Text>
            )}
          </Row>
        )}
        {hasHeaderRow && (
          <Row gap={2} align="center">
            {header &&
              (isCompact ? (
                <Text size="sm" fontWeight="semibold">
                  {header}
                </Text>
              ) : (
                <Heading size="lg">{header}</Heading>
              ))}
            {headerBadgeText && (
              <Badge variant={headerBadgeVariant || "success"}>
                {headerBadgeText}
              </Badge>
            )}
          </Row>
        )}
        {description && <Text color="text.secondary">{description}</Text>}
        {hasChildren && (
          <Column gap={2} sx={{ "& > p": { margin: 0 } }}>
            {children}
          </Column>
        )}
      </Column>
    </AppCard>
  );
};

const Card = ({
  label,
  header,
  description,
  headerBadgeText,
  headerBadgeVariant,
  isCompact,
  url,
  children,
}: {
  // `header` is optional: a card may instead be composed from children (a
  // <stat>, a chart, pills). The attribute shorthand and the children body
  // can also be combined — attributes render as an eyebrow/header above.
  label?: string;
  header?: string;
  description?: string;
  headerBadgeText?: string;
  headerBadgeVariant?: "success" | "error";
  isCompact?: boolean;
  url?: string;
  children?: React.ReactNode;
}) => {
  const safeUrl = url ? parseSafeHttpUrl(url) : null;
  const hasHeaderRow = Boolean(header || headerBadgeText);
  const hasChildren = childrenHaveContent(children);
  // Attribute-only cards use a tight gap; nested content needs looser spacing.
  const columnGap = hasChildren ? 2 : 1;

  return (
    <AppCard
      p={isCompact ? 2 : 3}
      onClick={
        safeUrl
          ? () => window.open(safeUrl.href, "_blank", "noopener,noreferrer")
          : undefined
      }
      _hover={
        safeUrl
          ? { borderColor: "gray.400", bg: "base.lightBackground" }
          : undefined
      }
    >
      <Column gap={columnGap}>
        {label && (
          <Text size="sm" color="text.secondary">
            {label}
          </Text>
        )}
        {hasHeaderRow && (
          <Row gap={2} align="center">
            {header && (
              <Box
                sx={{
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                }}
              >
                <Text size="md" fontWeight="medium">
                  {header}
                </Text>
              </Box>
            )}
            {headerBadgeText && (
              <Box
                as={Badge}
                variant={headerBadgeVariant || "success"}
                size="sm"
                flexShrink={0}
              >
                {headerBadgeText}
              </Box>
            )}
          </Row>
        )}
        {description && (
          <Text size="sm" color="text.secondary">
            {description}
          </Text>
        )}
        {hasChildren && (
          // Strip react-markdown's default paragraph margins so nested prose,
          // stats, and charts sit flush within the card's own gap spacing.
          <Column gap={2} sx={{ "& > p": { margin: 0 } }}>
            {children}
          </Column>
        )}
        {safeUrl && (
          <Row align="center" gap={1} mt={1} sx={{ minWidth: 0 }}>
            <Box as={ExternalLinkIcon} flexShrink={0} color="primary.base" />
            <TextWithTooltip size="sm" color="primary.base" fontWeight="medium">
              {safeUrl.domain}
            </TextWithTooltip>
          </Row>
        )}
      </Column>
    </AppCard>
  );
};

// react-markdown hands custom elements a `children` that is often just
// whitespace text nodes (the newlines between tags). Treat those as empty so
// an attribute-only card doesn't render a stray blank body row.
const childrenHaveContent = (children: React.ReactNode): boolean =>
  React.Children.toArray(children).some(
    (child) => typeof child !== "string" || child.trim().length > 0,
  );

// Variant values arrive as untyped HTML attribute strings, so every variant
// must be coerced to its component's allowed set before reaching Badge —
// falling back rather than passing through whatever the agent wrote.
const coerceVariant =
  <V extends string>(allowed: ReadonlySet<V>, fallback: V) =>
  (variant?: string): V =>
    variant && (allowed as ReadonlySet<string>).has(variant)
      ? (variant as V)
      : fallback;

type PillVariant =
  | "success"
  | "error"
  | "warning"
  | "info"
  | "subtle"
  | "upsell";

const PILL_VARIANTS = new Set<PillVariant>([
  "success",
  "error",
  "warning",
  "info",
  "subtle",
  "upsell",
]);

const pillVariant = coerceVariant(PILL_VARIANTS, "subtle");

// The inline text badge is deliberately limited to green/red/gray (subtle),
// matching the headline badge's palette. Anything else falls back to gray.
type InlineBadgeVariant = "success" | "error" | "subtle";

const INLINE_BADGE_VARIANTS = new Set<InlineBadgeVariant>([
  "success",
  "error",
  "subtle",
]);

const inlineBadgeVariant = coerceVariant(INLINE_BADGE_VARIANTS, "subtle");

// Badges inside the box-content components (<stat>, <headline>) are limited
// to green/red. An absent variant defaults to success (the badge usually
// carries a positive delta), but an unknown one falls back to neutral gray
// rather than guessing a sentiment.
type BoxBadgeVariant = "success" | "error" | "subtle";

const KNOWN_BOX_BADGE_VARIANTS = new Set<BoxBadgeVariant>(["success", "error"]);

const knownBoxBadgeVariant = coerceVariant(KNOWN_BOX_BADGE_VARIANTS, "subtle");

const boxBadgeVariant = (variant?: string): BoxBadgeVariant =>
  variant ? knownBoxBadgeVariant(variant) : "success";

const INLINE_BADGE_STYLES: Record<
  InlineBadgeVariant,
  { bg: string; color: string }
> = {
  success: { bg: "green.50", color: "text.success" },
  error: { bg: "red.50", color: "text.danger" },
  subtle: { bg: "gray.100", color: "text.primary" },
};

// A tight inline chip, unlike the Badge component (whose pill radius, border,
// and fixed font size inflate the line when embedded mid-sentence). Font size
// is em-relative so the chip tracks the surrounding prose size.
const InlineTextBadge = ({
  variant,
  children,
}: {
  variant: InlineBadgeVariant;
  children: React.ReactNode;
}) => {
  const style = INLINE_BADGE_STYLES[variant];
  return (
    <Box
      as="span"
      px={1.5}
      py="1px"
      borderRadius="md"
      bg={style.bg}
      color={style.color}
      fontWeight="semibold"
      fontSize="0.9em"
      whiteSpace="nowrap"
    >
      {children}
    </Box>
  );
};

// Phrase-led box-content block: a short header with an optional colored badge
// and a description below, meant to be nested as a card child alongside
// <stat>. The number, if any, lives in the badge.
const Headline = ({
  header,
  description,
  badgeText,
  badgeVariant,
}: {
  header?: string;
  description?: string;
  badgeText?: string;
  badgeVariant: BoxBadgeVariant;
}) => {
  return (
    <Column gap={1}>
      {(header || badgeText) && (
        <Row gap={2} align="center">
          {header && (
            <Box
              sx={{
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
            >
              <Text size="md" fontWeight="medium">
                {header}
              </Text>
            </Box>
          )}
          {badgeText && (
            <Box as={Badge} variant={badgeVariant} size="sm" flexShrink={0}>
              {badgeText}
            </Box>
          )}
        </Row>
      )}
      {description && (
        <Text size="sm" color="text.secondary">
          {description}
        </Text>
      )}
    </Column>
  );
};

// Number tints: use success.base rather than text.success, which reads too
// dark at heading size.
const STAT_VALUE_COLORS: Record<"success" | "error", string> = {
  success: "success.base",
  error: "text.danger",
};

const Stat = ({
  label,
  value,
  valueVariant,
  description,
  badgeText,
  badgeVariant,
  isCompact,
}: {
  label?: string;
  // Attrs arrive untyped from react-markdown — degrade to no heading rather
  // than rendering a blank one if `value` is ever absent.
  value?: string;
  valueVariant?: "success" | "error";
  description?: string;
  badgeText?: string;
  badgeVariant: BoxBadgeVariant;
  isCompact?: boolean;
}) => {
  const valueColor = valueVariant ? STAT_VALUE_COLORS[valueVariant] : undefined;
  return (
    <Column gap={1}>
      {label && (
        <Text size="sm" color="text.secondary">
          {label}
        </Text>
      )}
      <Row gap={2} align="baseline">
        {value && (
          <Heading size={isCompact ? "lg" : "2xl"} color={valueColor}>
            {value}
          </Heading>
        )}
        {description && (
          <Text size="sm" color="text.secondary">
            {description}
          </Text>
        )}
        {badgeText && (
          <Box as={Badge} variant={badgeVariant} size="sm" flexShrink={0}>
            {badgeText}
          </Box>
        )}
      </Row>
    </Column>
  );
};
